import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { evaluationAPI, evaluationTypeAPI } from '../../api/services';

const AdminEvalModal = React.lazy(() => import('./AdminEvaluationModal'));

const cap = (s: string) => s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ') : '';
const fmtDate = (d?: string) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

const AdminEvaluationsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [evaluations, setEvaluations] = useState<any[]>([]);
  const [periods, setPeriods] = useState<string[]>([]);
  const [evalTypes, setEvalTypes] = useState<any[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [sortBy, setSortBy] = useState('updatedAt');
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const perPage = 25;

  const evalParam = searchParams.get('eval');
  const [selectedEvalId, setSelectedEvalId] = useState<string | null>(evalParam);

  const fetchData = useCallback(async (opts?: { period?: string; typeId?: string; status?: string; sort?: string; order?: string }) => {
    setLoading(true);
    try {
      const params: any = {};
      if (opts?.period) params.evaluationPeriod = opts.period;
      if (opts?.typeId) params.evaluationTypeId = opts.typeId;
      if (opts?.status) params.status = opts.status;
      params.sortBy = opts?.sort || 'updatedAt';
      params.sortOrder = opts?.order || 'desc';

      const [evalRes, periodsRes, typesRes] = await Promise.allSettled([
        evaluationAPI.getAllEvaluations(params),
        evaluationAPI.getAllEvaluationPeriods(),
        evaluationTypeAPI.getAllEvaluationTypes(true),
      ]);
      if (evalRes.status === 'fulfilled') {
        const data = evalRes.value.data?.data || evalRes.value.data || [];
        setEvaluations(Array.isArray(data) ? data : []);
      }
      if (periodsRes.status === 'fulfilled') {
        const p = periodsRes.value.data?.data || periodsRes.value.data || [];
        const periodList = Array.isArray(p) ? p : [];
        setPeriods(periodList);
        if (!opts?.period && periodList.length > 0) setSelectedPeriod(periodList[0]);
      }
      if (typesRes.status === 'fulfilled') {
        const t = typesRes.value.data?.data || typesRes.value.data || [];
        setEvalTypes(Array.isArray(t) ? t : []);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const refetch = (overrides?: { period?: string; typeId?: string; status?: string; sort?: string; order?: string }) => {
    const opts = {
      period: overrides?.period ?? selectedPeriod,
      typeId: overrides?.typeId ?? selectedTypeId,
      status: overrides?.status ?? selectedStatus,
      sort: overrides?.sort ?? sortBy,
      order: overrides?.order ?? sortOrder,
    };
    fetchData(opts);
  };

  const handlePeriodChange = (p: string) => { setSelectedPeriod(p); setPage(1); refetch({ period: p }); };
  const handleTypeChange = (id: string) => { setSelectedTypeId(id); setPage(1); refetch({ typeId: id }); };
  const handleStatusChange = (s: string) => { setSelectedStatus(s); setPage(1); refetch({ status: s }); };
  const handleSortChange = (field: string) => {
    const newOrder = sortBy === field && sortOrder === 'desc' ? 'asc' : 'desc';
    setSortBy(field);
    setSortOrder(newOrder);
    refetch({ sort: field, order: newOrder });
  };

  const openEval = (id: string) => {
    setSelectedEvalId(id);
    setSearchParams(prev => { const next = new URLSearchParams(prev); next.set('eval', id); return next; }, { replace: true });
  };

  const closeEval = () => {
    setSelectedEvalId(null);
    setSearchParams(prev => { const next = new URLSearchParams(prev); next.delete('eval'); return next; }, { replace: true });
    refetch();
  };

  const filtered = useMemo(() => evaluations.filter(e => {
    const name = (e.userId as any)?.name || '';
    return !search || name.toLowerCase().includes(search.toLowerCase());
  }), [evaluations, search]);

  const submitted = filtered.filter(e => e.status === 'submitted').length;
  const drafts = filtered.filter(e => e.status === 'draft').length;
  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  useEffect(() => { setPage(1); }, [search]);

  const SortArrow = ({ field }: { field: string }) => {
    if (sortBy !== field) return <span className="text-gray-300 ml-0.5">&#8597;</span>;
    return <span className="text-blue-500 ml-0.5">{sortOrder === 'asc' ? '&#8593;' : '&#8595;'}</span>;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <h1 className="text-xl font-semibold text-gray-900">Evaluations</h1>
        <p className="text-sm text-gray-400 mt-0.5">Review and score employee evaluations</p>
      </div>

      {/* Filters row */}
      <div className="px-6 py-3 border-b border-gray-100 flex items-center justify-between gap-3 flex-wrap">
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name..."
          className="bg-gray-100 rounded-lg px-3 py-1.5 text-sm w-64 outline-none focus:ring-1 focus:ring-gray-300" />

        <div className="flex items-center gap-3 flex-wrap">
          {/* Stats */}
          <div className="flex items-center gap-4 text-xs text-gray-400">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-blue-500" />
              <span className="text-sm font-semibold text-gray-500">{submitted}</span>
              <span>submitted</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-gray-400" />
              <span className="text-sm font-semibold text-gray-500">{drafts}</span>
              <span>drafts</span>
            </div>
            <span className="text-gray-300">·</span>
            <span>{filtered.length} total</span>
          </div>

          {/* Evaluation type filter */}
          {evalTypes.length > 0 && (
            <select value={selectedTypeId} onChange={e => handleTypeChange(e.target.value)}
              className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-gray-300">
              <option value="">All types</option>
              {evalTypes.map((t: any) => <option key={t._id} value={t._id}>{t.name}</option>)}
            </select>
          )}

          {/* Status filter */}
          <select value={selectedStatus} onChange={e => handleStatusChange(e.target.value)}
            className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-gray-300">
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="submitted">Submitted</option>
            <option value="reviewed">Reviewed</option>
          </select>

          {/* Period filter */}
          {periods.length > 0 && (
            <select value={selectedPeriod} onChange={e => handlePeriodChange(e.target.value)}
              className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-gray-300">
              {periods.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          )}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20"><div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" /></div>
      ) : (
        <div>
          <div className="grid grid-cols-12 gap-1 px-6 py-2 text-xs text-gray-400 font-medium select-none border-b border-gray-100">
            <div className="col-span-3">Employee</div>
            <div className="col-span-2">Level</div>
            <div className="col-span-2 text-right cursor-pointer hover:text-gray-600" onClick={() => handleSortChange('totalScore')}>
              Emp Score <SortArrow field="totalScore" />
            </div>
            <div className="col-span-2 text-right cursor-pointer hover:text-gray-600" onClick={() => handleSortChange('adminTotalScore')}>
              Admin Score <SortArrow field="adminTotalScore" />
            </div>
            <div className="col-span-1">Status</div>
            <div className="col-span-2 text-right cursor-pointer hover:text-gray-600" onClick={() => handleSortChange('updatedAt')}>
              Modified <SortArrow field="updatedAt" />
            </div>
          </div>

          {paginated.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-sm font-medium text-gray-700">No evaluations found</p>
              <p className="text-xs text-gray-400 mt-1">{search ? 'Try a different search' : 'No evaluations for this period'}</p>
            </div>
          ) : paginated.map((e: any) => {
            const u = e.userId || {};
            return (
              <div key={e._id} onClick={() => openEval(e._id)}
                className="grid grid-cols-12 gap-1 px-6 py-2.5 border-b border-gray-100 cursor-pointer hover:bg-gray-50/80 transition-colors">
                <div className="col-span-3 flex items-center gap-2.5 min-w-0">
                  <div className="w-6 h-6 rounded-full bg-blue-500 text-white text-[10px] flex items-center justify-center shrink-0">{(u.name || '?')[0].toUpperCase()}</div>
                  <span className="text-sm text-gray-700 truncate capitalize">{u.name || 'Unknown'}</span>
                </div>
                <div className="col-span-2 flex items-center text-xs text-gray-500">{u.level || '—'}</div>
                <div className="col-span-2 flex items-center justify-end text-xs text-blue-600 tabular-nums">{e.totalScore != null ? Number(e.totalScore).toFixed(2) : '—'}</div>
                <div className="col-span-2 flex items-center justify-end text-xs text-gray-700 tabular-nums">{e.adminTotalScore != null ? Number(e.adminTotalScore).toFixed(2) : '—'}</div>
                <div className="col-span-1 flex items-center">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${e.status === 'submitted' ? 'bg-blue-50 text-blue-700' : e.status === 'reviewed' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{cap(e.status || '')}</span>
                </div>
                <div className="col-span-2 flex items-center justify-end text-xs text-gray-400">{fmtDate(e.updatedAt)}</div>
              </div>
            );
          })}

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-3">
              <span className="text-xs text-gray-400">{(page - 1) * perPage + 1}–{Math.min(page * perPage, filtered.length)} of {filtered.length}</span>
              <div className="flex items-center gap-1">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="text-xs px-2.5 py-1 rounded border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">Prev</button>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                  className="text-xs px-2.5 py-1 rounded border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">Next</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Eval detail modal */}
      <React.Suspense fallback={null}>
        {selectedEvalId && (
          <AdminEvalModal evaluationId={selectedEvalId} onClose={closeEval} onRefresh={() => refetch()} />
        )}
      </React.Suspense>
    </div>
  );
};

export default AdminEvaluationsPage;
